import { useCallback, useEffect, useRef, useState } from "react";
import {
  fetchMarketPulse,
  getCachedCurrentMarketPulse,
  refreshCurrentMarketPulse,
} from "utils/fetchMarketPulse";
import {
  normalizeHistoricalMarketPulseResponse,
  normalizeMarketPulseResponse,
} from "utils/normalizeMarketPulse";

/**
 * Shared Market Pulse data hook for Explore (current) and Hub (historical).
 *
 * @param {{ type?: "current"|"historical", autoFetch?: boolean }} options
 */
export const useFetch = ({ type = "current", autoFetch = true } = {}) => {
  const isCurrent = type === "current";
  const cancelledRef = useRef(false);

  const [data, setData] = useState(() =>
    isCurrent ? getCachedCurrentMarketPulse() : null,
  );
  const [pulses, setPulses] = useState(() =>
    isCurrent
      ? normalizeMarketPulseResponse(getCachedCurrentMarketPulse())
      : [],
  );
  const [loading, setLoading] = useState(() =>
    isCurrent ? !getCachedCurrentMarketPulse() : true,
  );
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);

  const applyCurrentResponse = useCallback((response) => {
    setData(response);
    setPulses(normalizeMarketPulseResponse(response));
  }, []);

  const fetchCurrent = useCallback(async ({ force = true } = {}) => {
    const cached = getCachedCurrentMarketPulse();
    if (cached) {
      applyCurrentResponse(cached);
      setLoading(false);
    } else {
      setLoading(true);
    }

    setError(null);

    try {
      const response = force
        ? await refreshCurrentMarketPulse()
        : await fetchMarketPulse({ type: "current" });

      if (cancelledRef.current) return;

      if (response) {
        applyCurrentResponse(response);
        setError(null);
      } else if (!getCachedCurrentMarketPulse()) {
        setData(null);
        setPulses([]);
      }
    } catch (err) {
      console.error("Failed to load market pulse:", err);
      if (cancelledRef.current) return;

      setError(err);
      if (!getCachedCurrentMarketPulse()) {
        setData(null);
        setPulses([]);
      }
    } finally {
      if (!cancelledRef.current) {
        setLoading(false);
      }
    }
  }, [applyCurrentResponse]);

  const fetchHistorical = useCallback(async ({ cursor, append } = {}) => {
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
      setError(null);
    }

    try {
      const response = await fetchMarketPulse({
        type: "historical",
        cursor,
      });
      if (cancelledRef.current) return;

      const normalized = normalizeHistoricalMarketPulseResponse(response);

      setPulses((prev) =>
        append ? [...prev, ...normalized.pulses] : normalized.pulses,
      );
      setHasMore(normalized.pagination.hasMore);
      setNextCursor(normalized.pagination.nextCursor);
      setData(response);
      setError(null);
    } catch (err) {
      console.error("Failed to load historical market pulse:", err);
      if (cancelledRef.current) return;

      setError(err);
      if (!append) {
        setPulses([]);
        setHasMore(false);
        setNextCursor(null);
        setData(null);
      }
    } finally {
      if (!cancelledRef.current) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, []);

  const refetch = useCallback(() => {
    if (isCurrent) {
      return fetchCurrent({ force: true });
    }
    return fetchHistorical({ append: false });
  }, [isCurrent, fetchCurrent, fetchHistorical]);

  const loadMore = useCallback(() => {
    if (!hasMore || !nextCursor || loadingMore || isCurrent) return;
    return fetchHistorical({ cursor: nextCursor, append: true });
  }, [hasMore, nextCursor, loadingMore, isCurrent, fetchHistorical]);

  useEffect(() => {
    cancelledRef.current = false;

    if (!autoFetch) {
      return () => {
        cancelledRef.current = true;
      };
    }

    if (isCurrent) {
      fetchCurrent({ force: true });
    } else {
      fetchHistorical({ append: false });
    }

    return () => {
      cancelledRef.current = true;
    };
  }, [autoFetch, isCurrent, fetchCurrent, fetchHistorical]);

  return {
    data,
    pulses,
    loading,
    loadingMore,
    error,
    hasMore,
    nextCursor,
    refetch,
    loadMore,
  };
};

export default useFetch;
